<template>
  <div class="bg-gray-150 min-h-screen">
    <div class="flex flex-col max-w-6xl mx-auto bg-white shadow-md relative">
      <div
        class="flex flex-col lg:flex-row lg:py-10 px-6 lg:px-0 pt-0 pr-0 pl-0">
        <!-- Left Column -->
        <div class="md:w-1/2">
          <CustomSlider
            :slides="sliderImagesComputed"
            @open-lightbox="openLightbox"
            :autoplay="autoplayEnabled"
            :autoplaySpeed="5000"
            :pauseOnHover="true"
            :isFirstSlider="true"
            :bulletOffset="50"
            :playButtonOffset="35"
            :isLightboxOpen="isLightboxOpen"
            ref="customSlider"
            class="max-w-full"
            @user-interaction="handleUserInteraction"
            @mouseenter="pauseAutoplay"
            @mouseleave="resumeAutoplay"
            key="custom-slider"
            :initialSlide="0"
          />
        </div>

        <!-- Right Column -->
        <div class="flex flex-col justify-center mt-5 lg:mt-0 md:w-1/2 px-10">
          <div class="flex flex-col space-y-6 mx-auto w-full">
            <div>
              <a v-if="campaign.parent" :href="'/campaigns/' + campaign.parent.id">
                <h2 class="text-pink-500 font-semibold text-left text-2xl uppercase">
                  {{ campaign.parent.name }}
                </h2>
              </a>
              <h3 class="font-medium text-left text-3xl">
                {{ campaign.name }}
              </h3>
            </div>

            <!-- Impact Score Badge -->
            <a
              v-if="campaign.impact_score && campaign.foundation_id"
              :href="impactScoreLeaderboardLink"
              class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-md text-sm"
              title="View Impact Leaderboard"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"></path>
              </svg>
              <span>Impact Score</span>
              <span class="mx-1.5">·</span>
              <span class="text-purple-100">{{ formattedImpactScore }}</span>
              <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
              </svg>
            </a>

            <standard-campaign-goal :campaign-id="campaign.id" :campaign="campaign"></standard-campaign-goal>

            <div>
              <my-donate-button :campaign-id="campaign.id" :pledge-id="pledgeId"
                                :campaign="campaign"></my-donate-button>
            </div>
          </div>
        </div>
      </div>

      <div
        class="relative bg-white pt-6 lg:pt-16 pb-10 lg:pb-20 px-10 anchor-link-padding" id="about"
      >
        <div class="max-w-6xl mx-auto">
          <div class="flex flex-col lg:flex-row lg:justify-between">
            <!-- First Column: At the beginning -->
            <div class="lg:flex-1 text-center lg:text-left mb-8 lg:mb-0">
              <share-link :campaign-id="campaign.id" :campaign="campaign" :show-header="true"></share-link>
            </div>

            <!-- Center Column: Takes up available space but with a maximum width -->
            <div class="lg:flex-auto mt-5 lg:mt-0 lg:px-10 mb-8 lg:mb-0">
              <h2 class="uppercase font-bold mb-6 text-blue text-2xl md:text-3xl">About Our Campaign</h2>
              <div class="campaign-message-wrapper">
                <campaign-page-message class="campaign-message" v-if="!campaign.isPledgeDrive" :message="campaign.message"></campaign-page-message>
                <campaign-pledge-drive-page-message class="campaign-message" v-if="campaign.isPledgeDrive" :message="campaign.message"></campaign-pledge-drive-page-message>
              </div>
            </div>

            <!-- Third Column (events section): At the end -->
            <div class="lg:flex-1">
              <highlighted-blog-posts :campaign-id="campaign.id" :campaign="campaign"></highlighted-blog-posts>
            </div>
          </div>
        </div>

      </div>

      <section
        v-if="dynamicImpactSlides.length > 0"
        class="bg-pink-100 anchor-link-padding"
        id="impact"
      >
        <div class="max-w-6xl mx-auto flex flex-col lg:flex-row justify-center relative gap-0 px-16 items-center">
          <CustomImpactSlider
            :slides="dynamicImpactSlides"
            :autoplay="true"
            @slide-changed="onImpactSlideChange"
            class="w-full h-full items-center"
          />
        </div>
      </section>

      <section class="flex flex-col px-11 md:px-6 py-20 mx-auto w-full lg:w-[64%] anchor-link-padding" id="activity">
        <h2 class="uppercase font-bold mb-8 text-blue text-4xl text-center">Our Campaign Activity</h2>
        <campaign-activities :campaign-id="campaignId"></campaign-activities>
      </section>

      <section
        class="bg-white w-full z-10 shadow-sm campaign-fixed-bar opacity-0 transition-opacity duration-400 ease-in-out campaign-bar max-w-6xl mx-auto left-0 right-0 block md:hidden">
          <my-sticky-donate-button :campaign-id="campaign.id" :pledge-id="pledgeId"
                                  :campaign="campaign"></my-sticky-donate-button>
      </section>
    </div>
  </div>
  <FsLightbox
    :toggler="toggler"
    :sources="lightboxSources"
    :types="lightboxTypes"
    :slide="currentSlide"
    :onSlideChange="pauseVideos"
  />
</template>

<script>
import { mapMutations } from 'vuex';
import HighlightedBlogPosts from '../campaign-page/highlighted-blog-posts/HighlightedBlogPosts'
import CampaignPageMessage from '../campaign-page/CampaignPageMessage'
import CampaignActivities from '../campaign-page/activities/CampaignActivities'
import StandardCampaignGoal from '../campaign-page/campaign-goal/StandardCampaignGoal'
import CampaignPledgeDrivePageMessage from "../campaign-page/CampaignPledgeDrivePageMessage"
import CampaignEventDisplay from "../campaign-page/events/CampaignEventDisplay"
import CustomSlider from '../../general/CustomSlider.vue'; // Adjust the path as needed
import CustomImpactSlider from '../../general/CustomImpactSlider.vue';
import { ref, nextTick, watch } from 'vue';

import FsLightbox from "fslightbox-vue/v3";

export default {
  name: 'PerfectPassCampaignPage',
  components: {
    CampaignPledgeDrivePageMessage,
    StandardCampaignGoal,
    CampaignPageMessage,
    HighlightedBlogPosts,
    CampaignActivities,
    CampaignEventDisplay,
    FsLightbox,
    CustomSlider,
    CustomImpactSlider
  },
  setup() {
    const isLightboxOpen = ref(false);

    return {
      isLightboxOpen,
    };
  },
  props: {
    campaignId: {
      type: String,
      required: true,
    },

    pledgeId: {
      required: false,
    },

    myCampaign: {
      type: Object,
      required: false,
    },
  },

  data() {
    return {
      isVisible: true,
      campaign: this.myCampaign,
      timer: null,
      timer1: null,
      imageRotationInterval: null,
      currentImpactSlideIndex: 0,
      toggler: false,
      currentSlide: 1,
      autoplayEnabled: true,
      userInteracted: false,
      sliderImages: [],
      impactSliderImages: [],
      campaignEvents: [],
    }
  },

  created() {
    this.isLoading = true

    this.emitter.on('campaign-donation-update', (campaign) => {
      this.campaign = campaign
    })

    this.captureView()

    this.timer = setInterval(() => {
      this.captureView()
    }, 30000);
    // }, 5000);

    if (this.campaign && this.campaign.id) {
      this.fetchCampaignData();
    }
  },

  beforeUnmount() {
    clearInterval(this.imageRotationInterval);
    clearInterval(this.timer)
    clearInterval(this.timer1)
  },
  methods: {
    async fetchCampaignData() {
      try {
        await Promise.all([
          this.fetchSliderImages(),
          this.fetchImpactSliderImages()
        ]);
      } catch (error) {
        console.error("Error fetching campaign data:", error);
      }
    },
    async fetchSliderImages() {
      try {
        const response = await axios.get(`/api/open/campaigns/${this.campaign.id}/slider-images`);
        this.sliderImages = this.processSliderImages(response.data.slider_images || []);

        if (this.sliderImages.length === 0 && this.campaign.foundation_id) {
          const foundationResponse = await axios.get(`/api/open/campaigns/${this.campaign.foundation_id}/slider-images`);
          this.sliderImages = this.processSliderImages(foundationResponse.data.slider_images || []);
        }
      } catch (error) {
        console.error("Error fetching slider images:", error);
        this.sliderImages = [];
      }
    },

    async fetchImpactSliderImages() {
      try {
        // Always use the foundation_id if it exists, otherwise use the current campaign id
        const foundationId = this.campaign.foundation_id || this.campaign.id;
        const response = await axios.get(`/api/open/campaigns/${foundationId}/impact-slider-images`);
        this.impactSliderImages = this.processImpactSliderImages(response.data.impact_slider_images || []);
      } catch (error) {
        console.error("Error fetching impact slider images:", error);
        this.impactSliderImages = [];
      }
    },

    async fetchCampaignEvents() {
      if (!this.campaign || !this.campaign.id) {
        console.error('Campaign ID is not available');
        return;
      }
      try {
        const response = await axios.get(`/campaign/${this.campaign.id}/events`);
        this.campaignEvents = response.data;
      } catch (error) {
        console.error('Error fetching campaign events:', error);
        console.log('Error response:', error.response);
        this.$notify({
          text: `Failed to fetch campaign events: ${error.response?.data?.message || error.message}`,
          type: 'error'
        });
      }
    },

    processSliderImages(images) {
      return images.map((item, index) => {
        let processedItem = { id: `slide-${index}` };

        if (item.type === 'video') {
          processedItem = {
            ...processedItem,
            src: item.videoUrl,
            thumbnail: item.thumbnailUrl,
            type: 'video'
          };
        } else if (item.type === 'youtube') {
          const videoID = this.extractYoutubeID(item.videoUrl);
          processedItem = {
            ...processedItem,
            src: `https://www.youtube.com/embed/${videoID}?enablejsapi=1&autoplay=1&mute=0`,
            thumbnail: item.thumbnailUrl || `https://img.youtube.com/vi/${videoID}/0.jpg`,
            type: 'youtube'
          };
        } else {
          processedItem = {
            ...processedItem,
            src: item.url,
            type: 'image'
          };
        }

        return processedItem;
      });
    },
    processImpactSliderImages(images) {
      return images.map((item, index) => ({
        id: `impact-slide-${index}`, // Add a unique id for each slide
        title: `Your Impact`,
        number: item.title,
        subtitle: item.subtitle,
        description: item.text,
        image: item.image,
      }));
    },
    pauseVideos(instance) {
      var sources = instance.elements.sources;
      for (var i = 0; i < sources.length; i++) {
        var source = sources[i];
        if (!source) {
          continue;
        }
        if (source.tagName === "VIDEO") {
          source.pause();
        } else if (source.tagName === "IFRAME") {
          source.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
        }
      }
    },
    onImpactSlideChange(index) {
      this.currentImpactSlideIndex = index;
    },
    prevImpactSlide() {
      this.currentImpactSlideIndex = (this.currentImpactSlideIndex - 1 + this.dynamicImpactSlides.length) % this.dynamicImpactSlides.length;
    },
    nextImpactSlide() {
      this.currentImpactSlideIndex = (this.currentImpactSlideIndex + 1) % this.dynamicImpactSlides.length;
    },
    triggerDonationModal() {
      this.emitter.emit('open-donation-modal-from-campaign');
    },
    visibilityChanged(isVisible, entry) {
      this.isVisible = isVisible
    },
    captureView() {
      try {
        axios.post('/capture-view', {id: this.campaign.id})
            .then(response => {
              // console.log(response)
            })
      } catch (error) {
        clearInterval(this.timer)
      }
    },

    openLightbox(index) {
      // Adjust index for looping
      const adjustedIndex = index % this.sliderImages.length;
      this.currentSlide = adjustedIndex + 1; // FsLightbox uses 1-based index
      this.toggler = !this.toggler;
      this.isLightboxOpen = true;

      nextTick(() => {
        this.handleLightboxContent(adjustedIndex);
      });
    },

    closeLightbox() {
      this.isLightboxOpen = false;
      this.toggler = false;
    },

    handleLightboxContent(activeIndex) {
      const lightboxSources = document.querySelectorAll('.fslightbox-source');
      lightboxSources.forEach((source, i) => {
        const slide = this.sliderImages[i % this.sliderImages.length];
        if (slide.type === 'youtube' && source.tagName === 'IFRAME') {
          let src = source.src;
          if (i === activeIndex) {
            src = src.replace('autoplay=0', 'autoplay=1');
            src = src.replace('mute=1', 'mute=0');
            source.src = src;
            // Use a timeout to ensure the iframe has loaded
            setTimeout(() => {
              source.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            }, 1000);
          } else {
            source.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
          }
        }
      });
    },

    handleVideoStates(activeIndex) {
      const lightboxSources = document.querySelectorAll('.fslightbox-source');
      lightboxSources.forEach((source, i) => {
        if (source.tagName === 'IFRAME') {
          const slide = this.sliderImages[i % this.sliderImages.length];
          if (slide.type === 'youtube') {
            let src = source.getAttribute('src');
            if (i === activeIndex) {
              // Unmute and play the active video
              src = src.replace('&mute=1', '&mute=0');
              source.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            } else {
              // Mute and pause other videos
              src = src.replace('&mute=0', '&mute=1');
              source.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
            }
            source.setAttribute('src', src);
          }
        }
      });
    },

    handleUserInteraction() {
      this.userInteracted = true;
      this.pauseAutoplay();
    },

    pauseAutoplay() {
      this.autoplayEnabled = false;
      if (this.$refs.customSlider) {
        this.$refs.customSlider.pauseAutoplay();
      }
    },

    resumeAutoplay() {
      if (!this.userInteracted) {
        this.autoplayEnabled = true;
        if (this.$refs.customSlider) {
          this.$refs.customSlider.resumeAutoplay();
        }
      }
    },

    resetAutoplay() {
      this.userInteracted = false;
      this.autoplayEnabled = true;
      if (this.$refs.customSlider) {
        this.$refs.customSlider.startAutoplay();
      }
    },

    playYouTubeVideo(iframe) {
      // Remove existing onload listener
      iframe.onload = null;

      // Set up a new onload listener
      iframe.onload = () => {
        // This will run after the iframe has loaded
        iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
      };

      // Update the src to trigger a reload and the onload event
      let src = iframe.src;
      src = src.replace('autoplay=0', 'autoplay=1');
      src = src.replace('mute=1', 'mute=0');
      iframe.src = src;
    },

    pauseYouTubeVideo(iframe) {
      iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    },

    pauseAllVideosExcept(activeIndex) {
      const lightboxSources = document.querySelectorAll('.fslightbox-source');
      lightboxSources.forEach((source, i) => {
        if (i !== activeIndex && source.tagName === 'IFRAME') {
          // Pause the video by reloading its src
          source.src = source.src;
        }
      });
    },
    extractYoutubeID(url) {
        const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length == 11) ? match[2] : null;
    }
  },

  watch: {
    toggler(newValue) {
      if (!newValue) {
        this.closeLightbox();
      }
    }
  },

  async mounted() {
    this.$store.commit('campaignWidget/setCampaignName', this.campaign.name);

    if (this.campaign.parent) {
      this.$store.commit('campaignWidget/setCampaignParentName', this.campaign.parent.name);
    } else {
      this.$store.commit('campaignWidget/setCampaignParentName', null);
    }

    this.$store.commit('campaignWidget/setCampaign', this.campaign);
    this.$store.commit('campaignWidget/setPledgeId', this.pledgeId);

    this.resetAutoplay();

// toggle class on sticky bar based on scroll location
    const campaignButtons = document.querySelector(".campaign-buttons")
    const campaignFixedBar = document.querySelector(".campaign-fixed-bar")
    const add_class_on_scroll = () => campaignFixedBar.classList.add("opacity-100", "active", "fixed")
    const remove_class_on_scroll = () => campaignFixedBar.classList.remove("opacity-100", "active", "fixed")
    window.onscroll = function () {
      if (campaignButtons.getBoundingClientRect().bottom <= 0) {
        add_class_on_scroll();
      } else {
        remove_class_on_scroll();
      }
    }

    this.isLoading = false

    this.timer1 = setInterval(() => {
      this.progressCounter = this.campaign.funded
    }, 1000)

  },

  computed: {
    dynamicImpactSlides() {
      return this.impactSliderImages;
    },

    heroBackgroundClass() {
      const availableClasses = [
        'hero-purple-blue',
        'hero-blue',
        'hero-green',
        'hero-pink',
        'hero-red',
      ]

      return availableClasses[Math.floor(Math.random() * availableClasses.length)]
    },

    heroBackgroundStyle() {
      return `linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, .5) 70%, rgba(0, 0, 0, .5) 100%), url('` + this.campaign.hero_image_path + `')`
    },

    sliderImagesComputed() {
      return this.sliderImages;
    },

    lightboxSources() {
      return this.sliderImages.map(slide => {
        if (slide.type === 'youtube') {
          return slide.src + '&autoplay=1';
        }
        return slide.src;
      });
    },

    lightboxTypes() {
      return this.sliderImages.map(slide => slide.type);
    },

    impactScoreLeaderboardLink() {
      // If parent_campaign_id is null, this is a parent or foundation campaign
      // Link to its own impact page
      if (!this.campaign.parent_campaign_id) {
        return `/campaigns/${this.campaign.id}/impact#leaderboard`;
      }
      // Otherwise, this is a child/grandchild - link to the top-level parent
      return `/campaigns/${this.campaign.parent_campaign_id}/impact#leaderboard`;
    },

    formattedImpactScore() {
      if (!this.campaign.impact_score) return null;
      return Number(this.campaign.impact_score).toFixed(1);
    },
  },

}
</script>

<style>
.carousel-item {
  position: relative;
  cursor: pointer;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 48px;
  color: white;
}
.slide-fade-enter-active, .slide-fade-leave-active {
  transition: opacity 1s;
}

.slide-fade-enter-from, .slide-fade-leave-to {
  opacity: 0;
}

.slide-fade-leave-from, .slide-fade-enter-to {
  opacity: 1;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.8s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.fade-leave-from, .fade-enter-to {
  opacity: 1;
}

/* Add this to make sure your images can overlap */
.absolute-image {
  position: absolute;
  top: 0;
  left: 0;
}
.far.fa-circle {
  stroke-width: 0.05em !important;  /* Adjust this to make it as thin as you like */
}
.campaign-message p {
  font-size: 1.25rem;
  line-height: 1.5;
}
.campaign-message p a {
  color: #DA1884;
  text-decoration: underline;
  transition: color 0.3s ease-in-out;
}
.campaign-message p a:hover {
  color: #B60E63 !important;
}
.campaign-message ul {
  list-style-type: disc;
  padding-left: 2rem;
  margin-bottom: 1.5rem;
}
.campaign-message ul li {
  font-size: 1.25rem;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.swiper-button-prev,
.swiper-button-next {
  color: #fff;
  width: auto;
  height: auto;
  position: unset;
  left: unset;
  right: unset;
  top: unset;
  bottom: unset;
}

.swiper-button-prev .fa-stack-2x,
.swiper-button-next .fa-stack-2x {
  color: #fff;
  opacity: 1;
}

.swiper-button-prev .fa-stack-1x,
.swiper-button-next .fa-stack-1x {
  color: #fff;
}

.swiper-button-next:after, .swiper-rtl .swiper-button-prev:after,
.swiper-button-prev:after, .swiper-rtl .swiper-button-next:after {
  display: none;
}

.swiper-pagination-bullet {
  background: #fefefe;
}

.swiper-pagination-bullet-active {
  background: #fff;
}

.navigation-wrapper {
  position: absolute;
  left: 50px;
  bottom: 20px;
  display: flex;
}

.swiper-pagination-bullets {
  left: -30px !important;
}

.custom-slide {
  opacity: 0 !important;
  transition: opacity .5s ease-in-out !important;
}

.custom-slide.swiper-slide-active {
  opacity: 1 !important;
}

.campaign-message-wrapper iframe {
  max-width: 100%;
}

.custom-pagination {
  position: absolute;
  bottom: -30px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-pagination .swiper-pagination-bullet {
  width: 20px;
  height: 20px;
  background-color: #DA1884;
  opacity: 0.4;
  margin: 0 6px;
  border-radius: 50%;
  transition: opacity 0.3s;
  cursor: pointer;
}

.custom-pagination .swiper-pagination-bullet-active {
  opacity: 1;
  background-color: #DA1884;
}

@media (max-width: 768px) {
  .swiper-pagination-bullets {
    left: 0 !important;
  }
}

/* Add these styles for the impact slider transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
